import { useState } from 'react'
import {
  IoPersonOutline,
  IoAdd,
  IoSearchOutline,
  IoDocumentTextOutline,
  IoDownloadOutline,
  IoTrashOutline,
} from 'react-icons/io5'
import { AnimatePresence, motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { usePatients, useDeletePatient } from '../hooks/usePatients'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { ErrorMessage } from '../components/ui/ErrorMessage'
import PatientDocumentsModal from '../components/PatientDocumentsModal'
import type { Patient } from '../types'
import { api } from '../services/api/client'

const getInitials = (firstName: string, lastName: string) =>
  `${lastName[0] ?? ''}${firstName[0] ?? ''}`.toUpperCase()

async function downloadCsv() {
  try {
    const response = await api.get('/patients/export/csv', { responseType: 'blob' })
    const blob = new Blob([response.data as BlobPart], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `patients_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success('Export CSV téléchargé')
  } catch {
    toast.error("Erreur lors de l'export CSV")
  }
}

export default function PatientsList() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [exportingCsv, setExportingCsv] = useState(false)

  const {
    data: paginatedPatients,
    isLoading,
    error,
    refetch,
  } = usePatients({ limit: 50 })
  const deletePatient = useDeletePatient()

  const patients = paginatedPatients?.data ?? []
  const totalPatients = paginatedPatients?.pagination?.totalItems ?? 0

  const filteredPatients = patients.filter((patient: Patient) => {
    const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase()
    return fullName.includes(searchQuery.toLowerCase())
  })

  const handleDelete = async (patient: Patient) => {
    const name = `${patient.firstName} ${patient.lastName}`.trim()
    if (!window.confirm(`Supprimer définitivement le patient ${name} ?`)) return
    await deletePatient.mutateAsync(patient.id)
  }

  if (error) {
    return (
      <div className='min-h-full pt-16 pb-0 px-6'>
        <div className='max-w-4xl mx-auto'>
          <ErrorMessage
            message='Impossible de charger la liste des patients'
            onRetry={() => {
              void refetch()
            }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className='min-h-full'>
      <div className='p-6 lg:p-8 max-w-7xl mx-auto'>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className='mb-8'
        >
          <h1 className='text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1'>
            Patients
          </h1>
          <p className='text-gray-500 dark:text-gray-400'>
            {isLoading ? '...' : totalPatients}{' '}
            {totalPatients === 1 ? 'patient enregistré' : 'patients enregistrés'}
          </p>
        </motion.div>

        {/* Barre de recherche et bouton */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className='flex flex-col sm:flex-row gap-3 mb-8'
        >
          <div className='relative flex-1 flex items-center'>
            <IoSearchOutline className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-gray-400 shrink-0' />
            <input
              type='text'
              placeholder='Rechercher un patient...'
              value={searchQuery}
              onChange={event => setSearchQuery(event.target.value)}
              className='input-field input-with-icon-left-lg w-full pr-4 py-3'
            />
          </div>
          <button
            onClick={() => {
              setExportingCsv(true)
              void downloadCsv().finally(() => setExportingCsv(false))
            }}
            disabled={exportingCsv}
            className='btn-secondary flex items-center justify-center gap-2 px-4 py-3 whitespace-nowrap'
          >
            {exportingCsv ? <LoadingSpinner size='sm' /> : <IoDownloadOutline className='text-xl' />}
            Export CSV
          </button>
          <Link
            to='/patients/new'
            className='btn-primary flex items-center justify-center gap-2 px-5 py-3 whitespace-nowrap'
          >
            <IoAdd className='text-xl' />
            Nouveau patient
          </Link>
        </motion.div>

        {/* Liste des patients */}
        {isLoading ? (
          <div className='flex justify-center py-20'>
            <LoadingSpinner size='lg' />
          </div>
        ) : (
          <AnimatePresence mode='wait'>
            {filteredPatients.length === 0 ? (
              <motion.div
                key='empty'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className='bg-white dark:bg-dark-50 rounded-2xl p-12 border border-gray-200 dark:border-gray-800 text-center'
              >
                <IoPersonOutline className='mx-auto mb-4 text-6xl text-gray-400' />
                <h3 className='mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100'>
                  {searchQuery ? 'Aucun patient trouvé' : 'Aucun patient'}
                </h3>
                <p className='mb-6 text-gray-500 dark:text-gray-400'>
                  {searchQuery
                    ? `Aucun patient ne correspond à « ${searchQuery} »`
                    : 'Commencez par ajouter votre premier patient'}
                </p>
                {!searchQuery && (
                  <Link
                    to='/patients/new'
                    className='btn-primary inline-flex items-center gap-2 px-5 py-3'
                  >
                    <IoAdd />
                    Ajouter un patient
                  </Link>
                )}
              </motion.div>
            ) : (
              <motion.div
                key='list'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'
              >
                {filteredPatients.map((patient: Patient, index: number) => (
                  <motion.div
                    key={patient.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.04 }}
                    whileHover={{ scale: 1.02, y: -4 }}
                    className='relative bg-white dark:bg-dark-50 rounded-2xl border border-gray-200 dark:border-gray-800 hover:border-primary-500/40 transition-all duration-200 shadow-card dark:shadow-card-dark overflow-hidden'
                  >
                    <Link to={`/patients/${patient.id}`} className='block p-5'>
                      <div className='flex items-center gap-3 mb-3'>
                        {/* Avatar initiales */}
                        <div className='flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-primary-500/20 to-primary-600/30 border border-primary-500/30 flex items-center justify-center'>
                          <span className='text-sm font-bold text-primary-400'>
                            {getInitials(patient.firstName, patient.lastName)}
                          </span>
                        </div>
                        <div className='min-w-0'>
                          <h3 className='font-bold text-gray-900 dark:text-gray-100 truncate'>
                            {patient.lastName.toUpperCase()} {patient.firstName}
                          </h3>
                          <p className='text-xs text-gray-500 dark:text-gray-400'>
                            {patient.age != null ? `${patient.age} ans` : 'Âge non renseigné'}
                          </p>
                        </div>
                      </div>
                      <div className='bg-gray-50 dark:bg-dark-100 rounded-lg px-3 py-2 border border-gray-100 dark:border-gray-700/50'>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mb-0.5'>
                          Pathologie
                        </p>
                        <p className='text-sm text-gray-700 dark:text-gray-300 font-medium truncate'>
                          {patient.disease || 'Aucune pathologie'}
                        </p>
                      </div>
                    </Link>

                    <div className='absolute top-4 right-4 flex items-center gap-1.5'>
                      <button
                        type='button'
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          setSelectedPatient(patient)
                          setIsModalOpen(true)
                        }}
                        className='bg-primary-500/10 hover:bg-primary-500/20 border border-primary-500/20 text-primary-400 p-1.5 rounded-lg transition-all'
                        title='Voir les documents'
                      >
                        <IoDocumentTextOutline className='text-base' />
                      </button>
                      <button
                        type='button'
                        onClick={e => {
                          e.preventDefault()
                          e.stopPropagation()
                          void handleDelete(patient)
                        }}
                        disabled={deletePatient.isPending}
                        className='bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-500 p-1.5 rounded-lg transition-all disabled:opacity-50'
                        title='Supprimer le patient'
                      >
                        <IoTrashOutline className='text-base' />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>

      {/* Modal Documents */}
      {selectedPatient && (
        <PatientDocumentsModal
          patient={selectedPatient}
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false)
            setSelectedPatient(null)
          }}
        />
      )}
    </div>
  )
}
